import React from 'react';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart, formatCOP } from '@/contexts/CartContext';
import { useLang } from '@/contexts/LanguageContext';
import { WHATSAPP_LINK } from '@/data/brand';

const SHIPPING_COP = 12000;

const buildWhatsAppMessage = (items, subtotal, lang) => {
  const productLines = items.map((it) => {
    const name = it.product.name[lang];
    const shadeTxt = it.shade ? ` — ${it.shade.label}${it.shade.ref ? ` (Ref. ${it.shade.ref})` : ''}` : '';
    return `• ${it.qty}x ${name}${shadeTxt} — ${formatCOP(it.lineTotal)}`;
  }).join('\n');
  const total = subtotal + SHIPPING_COP;
  if (lang === 'es') {
    return [
      '💕 ¡Hola Jelly!',
      '',
      'Quiero hacer este pedido:',
      '',
      '🛍 Producto(s):',
      productLines,
      '',
      `💰 Subtotal: ${formatCOP(subtotal)} COP`,
      `🚚 Envío nacional: ${formatCOP(SHIPPING_COP)} COP`,
      `💳 Total estimado: ${formatCOP(total)} COP`,
      '',
      'Por favor confirmar disponibilidad.',
      '',
      '✨ Para finalizar el pedido necesitamos tus datos:',
      '👤 Nombre completo:',
      '📱 Teléfono:',
      '🏙 Ciudad:',
      '📍 Dirección de envío:',
      '',
      '🙏 Gracias.',
    ].join('\n');
  }
  return [
    '💕 Hi Jelly!',
    '',
    'I would like to place this order:',
    '',
    '🛍 Product(s):',
    productLines,
    '',
    `💰 Subtotal: ${formatCOP(subtotal)} COP`,
    `🚚 National shipping: ${formatCOP(SHIPPING_COP)} COP`,
    `💳 Estimated total: ${formatCOP(total)} COP`,
    '',
    'Please confirm availability.',
    '',
    '✨ To finalize the order we need your info:',
    '👤 Full name:',
    '📱 Phone:',
    '🏙 City:',
    '📍 Shipping address:',
    '',
    '🙏 Thank you.',
  ].join('\n');
};

export default function CartDrawer() {
  const { items, count, subtotal, open, setOpen, setQty, remove, clear } = useCart();
  const { lang } = useLang();

  const checkout = () => {
    const msg = buildWhatsAppMessage(items, subtotal, lang);
    window.open(WHATSAPP_LINK(msg), '_blank');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
      <div
        className="absolute inset-0 bg-[#1F1A24]/40 backdrop-blur-sm animate-fade-up"
        onClick={() => setOpen(false)}
      />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[420px] bg-[#FFFBFB] shadow-2xl flex flex-col animate-fade-up">
        <header className="flex items-center justify-between px-6 py-5 border-b border-[#FF7597]/15">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#FF2D9C]" />
            <h2 className="font-display text-2xl font-black tracking-tighter text-[#1F1A24]">
              {lang === 'es' ? 'Tu carrito' : 'Your cart'}
            </h2>
            {count > 0 && (
              <span className="ml-1 text-xs font-bold tracking-widest text-[#574A66]">
                · {count} {count === 1 ? (lang === 'es' ? 'producto' : 'item') : (lang === 'es' ? 'productos' : 'items')}
              </span>
            )}
          </div>
          <button
            data-testid="cart-close"
            onClick={() => setOpen(false)}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-[#FF7597]/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <span className="h-16 w-16 rounded-full bg-[#FFF2F5] inline-flex items-center justify-center mb-5">
              <ShoppingBag className="w-7 h-7 text-[#FF7597]" />
            </span>
            <p className="font-display text-xl font-bold text-[#1F1A24]">
              {lang === 'es' ? 'Tu carrito está vacío' : 'Your cart is empty'}
            </p>
            <p className="mt-2 text-sm text-[#574A66] max-w-xs">
              {lang === 'es' ? 'Agrega productos para empezar tu pedido Jelly.' : 'Add products to start your Jelly order.'}
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {items.map((it) => (
              <article
                key={`${it.slug}-${it.shadeIndex}`}
                data-testid={`cart-item-${it.slug}-${it.shadeIndex}`}
                className="flex gap-3 rounded-2xl bg-white border border-[#FF7597]/10 p-3"
              >
                <div
                  className="h-20 w-20 rounded-xl overflow-hidden flex-shrink-0"
                  style={{ backgroundColor: it.product.color }}
                >
                  <img src={it.product.image} alt={it.product.name[lang]} className="w-full h-full object-contain p-1.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-sm leading-tight text-[#1F1A24] line-clamp-2">
                    {it.product.name[lang]}
                  </h3>
                  {it.shade && (
                    <p className="text-[10px] tracking-widest uppercase text-[#574A66] mt-0.5">
                      {it.shade.label}{it.shade.ref ? ` · Ref. ${it.shade.ref}` : ''}
                    </p>
                  )}
                  <p className="text-xs text-[#FF2D9C] font-bold mt-1">{formatCOP(it.unitPrice)}</p>

                  <div className="mt-2 flex items-center justify-between">
                    <div className="inline-flex items-center gap-1 rounded-full border border-[#FF7597]/30 bg-white">
                      <button
                        onClick={() => setQty(it.slug, it.shadeIndex, it.qty - 1)}
                        className="h-7 w-7 inline-flex items-center justify-center hover:text-[#FF2D9C]"
                        aria-label="Restar"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm font-bold w-6 text-center">{it.qty}</span>
                      <button
                        onClick={() => setQty(it.slug, it.shadeIndex, it.qty + 1)}
                        className="h-7 w-7 inline-flex items-center justify-center hover:text-[#FF2D9C]"
                        aria-label="Sumar"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button
                      onClick={() => remove(it.slug, it.shadeIndex)}
                      className="h-7 w-7 inline-flex items-center justify-center text-[#574A66] hover:text-red-500"
                      aria-label="Eliminar"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <footer className="border-t border-[#FF7597]/15 px-6 py-5 bg-white">
            <div className="space-y-1.5 mb-4">
              <div className="flex items-center justify-between text-xs text-[#574A66]">
                <span className="font-bold tracking-widest uppercase">
                  {lang === 'es' ? 'Subtotal' : 'Subtotal'}
                </span>
                <span className="tabular-nums">{formatCOP(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-[#574A66]">
                <span className="font-bold tracking-widest uppercase">
                  🚚 {lang === 'es' ? 'Envío nacional' : 'National shipping'}
                </span>
                <span className="tabular-nums">{formatCOP(SHIPPING_COP)}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-[#FF7597]/15">
                <span className="text-xs font-bold tracking-widest uppercase text-[#1F1A24]">
                  💳 {lang === 'es' ? 'Total estimado' : 'Estimated total'}
                </span>
                <span data-testid="cart-total-estimated" className="font-display text-2xl font-black tracking-tighter text-[#FF2D9C] tabular-nums">
                  {formatCOP(subtotal + SHIPPING_COP)}
                </span>
              </div>
            </div>
            <button
              data-testid="cart-checkout-whatsapp"
              onClick={checkout}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#25D366] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#1FB058] hover:scale-[1.02] transition-all shadow-[0_12px_30px_rgba(37,211,102,0.35)]"
            >
              <WhatsAppGlyph className="w-5 h-5" />
              {lang === 'es' ? 'Pedir por WhatsApp' : 'Order on WhatsApp'}
            </button>
            <button
              onClick={clear}
              className="mt-2 w-full text-xs text-[#574A66] hover:text-red-500"
            >
              {lang === 'es' ? 'Vaciar carrito' : 'Empty cart'}
            </button>
            <p className="mt-3 text-[10px] text-center text-[#574A66]">
              {lang === 'es'
                ? 'Confirmaremos disponibilidad, envío y forma de pago por WhatsApp.'
                : 'We will confirm availability, shipping and payment on WhatsApp.'}
            </p>
          </footer>
        )}
      </aside>
    </div>
  );
}

const WhatsAppGlyph = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.55 4.07 1.6 5.84L2 22l4.4-1.15a9.84 9.84 0 0 0 5.64 1.78h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.02A9.86 9.86 0 0 0 12.04 2z" />
  </svg>
);
